import React, { useState } from 'react';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { MediumText, RegularText, DemiLightText } from '../../lib/Text';
import SvgIcon from '../../components/SvgIcon';

// ── Types ──
type BusinessType = 'INDIVIDUAL' | 'INDIVIDUAL_BUSINESS' | 'CORPORATE';
type StepStatus = 'completed' | 'current' | 'pending';

interface CompanyForm {
    name: string;
    representativeName: string;
    businessRegistrationNumber: string;
    email: string;
    phone: string;
}

interface IndividualForm {
    name: string;
    email: string;
    phone: string;
}

interface AccountForm {
    bankCode: string;
    accountNumber: string;
    holderName: string;
}

// ── Constants ──
const BUSINESS_TYPES: { value: BusinessType; label: string }[] = [
    { value: 'INDIVIDUAL', label: '개인' },
    { value: 'INDIVIDUAL_BUSINESS', label: '개인사업자' },
    { value: 'CORPORATE', label: '법인사업자' },
];

const BANK_CODES = [
    { code: '04', name: 'KB국민은행' },
    { code: '88', name: '신한은행' },
    { code: '20', name: '우리은행' },
    { code: '81', name: '하나은행' },
    { code: '11', name: 'NH농협은행' },
    { code: '23', name: 'SC제일은행' },
    { code: '27', name: '한국씨티은행' },
    { code: '31', name: '대구은행' },
    { code: '32', name: '부산은행' },
    { code: '34', name: '광주은행' },
    { code: '35', name: '제주은행' },
    { code: '37', name: '전북은행' },
    { code: '39', name: '경남은행' },
    { code: '71', name: '우체국' },
    { code: '92', name: '토스뱅크' },
    { code: '89', name: '케이뱅크' },
    { code: '90', name: '카카오뱅크' },
];

const STEPS = [
    { label: '사업자 본인인증', description: '사업자 정보 인증' },
    { label: '계좌 정보확인', description: '정산 계좌 심사' },
    { label: '정산 계약 체결', description: '최종 계약 완료' },
];

// ── Helpers ──
const stripDashes = (value: string) => value.replace(/-/g, '');

// ── Sub Components ──
const FormField = ({
    label,
    required = true,
    children,
    error,
}: {
    label: string;
    required?: boolean;
    children: React.ReactNode;
    error?: string;
}) => (
    <Col className="flex flex-col mb-[20px]">
        <Row className="flex items-center mb-[6px]">
            <DemiLightText fontSize={14} lineHeight={20} color="#3D4244">
                {label}
            </DemiLightText>
            {required && (
                <span className="text-[#E54545] text-[14px] ml-[2px]">*</span>
            )}
        </Row>
        {children}
        {error && (
            <RegularText fontSize={12} lineHeight={16} color="#E54545" className="mt-[4px]">
                {error}
            </RegularText>
        )}
    </Col>
);

const TextInput = ({
    value,
    onChange,
    placeholder,
    maxLength,
}: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    maxLength?: number;
}) => (
    <input
        className="border border-[#E8EBF0] rounded-[8px] px-[14px] h-[44px] text-[14px] text-[#3D4244] outline-none focus:border-[#3D73DD] transition-colors"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
    />
);

const StepIndicator = ({ steps, statuses }: { steps: typeof STEPS; statuses: StepStatus[] }) => (
    <Col className="flex flex-col">
        {steps.map((step, index) => {
            const status = statuses[index];
            const isLast = index === steps.length - 1;
            return (
                <Row key={step.label} className="flex items-start">
                    {/* 원/체크 아이콘 + 연결선 */}
                    <Col className="flex flex-col items-center mr-[14px]">
                        <div
                            className={`w-[32px] h-[32px] rounded-full flex items-center justify-center ${
                                status === 'completed'
                                    ? 'bg-[#3D73DD]'
                                    : status === 'current'
                                    ? 'bg-[#3D73DD]'
                                    : 'bg-[#E8EBF0]'
                            }`}
                        >
                            {status === 'completed' ? (
                                <SvgIcon name="SvgCheck" size={16} fill="white" />
                            ) : (
                                <RegularText
                                    fontSize={14}
                                    lineHeight={20}
                                    color={status === 'current' ? 'white' : '#8B95A1'}
                                >
                                    {index + 1}
                                </RegularText>
                            )}
                        </div>
                        {!isLast && (
                            <div
                                className={`w-[2px] h-[40px] ${
                                    status === 'completed' ? 'bg-[#3D73DD]' : 'bg-[#E8EBF0]'
                                }`}
                            />
                        )}
                    </Col>
                    {/* 텍스트 */}
                    <Col className="flex flex-col pt-[4px]">
                        <MediumText
                            fontSize={15}
                            lineHeight={22}
                            color={status === 'pending' ? '#B6B8BA' : '#3D4244'}
                        >
                            {step.label}
                        </MediumText>
                        <RegularText
                            fontSize={13}
                            lineHeight={18}
                            color={status === 'pending' ? '#D1D5DB' : '#8B95A1'}
                        >
                            {step.description}
                        </RegularText>
                    </Col>
                </Row>
            );
        })}
    </Col>
);

// ── Main Component ──
const RappoPayDocumentsPage = () => {
    const authObject = getItemWithExpiration('authObject');

    const [businessType, setBusinessType] = useState<BusinessType>('INDIVIDUAL_BUSINESS');

    // company 폼 (개인사업자/법인사업자)
    const [company, setCompany] = useState<CompanyForm>({
        name: authObject?.centerName ?? '',
        representativeName: authObject?.name ?? '',
        businessRegistrationNumber: '',
        email: authObject?.email ?? '',
        phone: stripDashes(authObject?.phoneNumber ?? ''),
    });

    // individual 폼 (개인)
    const [individual, setIndividual] = useState<IndividualForm>({
        name: authObject?.name ?? '',
        email: authObject?.email ?? '',
        phone: stripDashes(authObject?.phoneNumber ?? ''),
    });

    // 계좌 정보 (공통)
    const [account, setAccount] = useState<AccountForm>({
        bankCode: '',
        accountNumber: '',
        holderName: '',
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    // TODO: API 연동 시 서버에서 상태 받아오기 (현재 모두 미완료)
    const stepStatuses: StepStatus[] = ['pending', 'pending', 'pending'];

    const isCompanyType = businessType === 'INDIVIDUAL_BUSINESS' || businessType === 'CORPORATE';

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (isCompanyType) {
            if (!company.name.trim()) newErrors['company.name'] = '사업자명을 입력해주세요';
            if (!company.representativeName.trim()) newErrors['company.representativeName'] = '대표자명을 입력해주세요';
            const bizNum = stripDashes(company.businessRegistrationNumber);
            if (!bizNum || bizNum.length !== 10 || !/^\d+$/.test(bizNum)) {
                newErrors['company.businessRegistrationNumber'] = '사업자번호 10자리를 입력해주세요';
            }
            if (!company.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(company.email)) {
                newErrors['company.email'] = '올바른 이메일을 입력해주세요';
            }
            const phone = stripDashes(company.phone);
            if (!phone || phone.length < 8 || !/^\d+$/.test(phone)) {
                newErrors['company.phone'] = '전화번호를 8자 이상 숫자로 입력해주세요';
            }
        } else {
            if (!individual.name.trim()) newErrors['individual.name'] = '이름을 입력해주세요';
            if (!individual.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(individual.email)) {
                newErrors['individual.email'] = '올바른 이메일을 입력해주세요';
            }
            const phone = stripDashes(individual.phone);
            if (!phone || phone.length < 8 || !/^\d+$/.test(phone)) {
                newErrors['individual.phone'] = '전화번호를 8자 이상 숫자로 입력해주세요';
            }
        }

        if (!account.bankCode) newErrors['account.bankCode'] = '은행을 선택해주세요';
        const acctNum = stripDashes(account.accountNumber);
        if (!acctNum || acctNum.length > 14 || !/^\d+$/.test(acctNum)) {
            newErrors['account.accountNumber'] = '계좌번호를 숫자로 입력해주세요 (최대 14자)';
        }
        if (!account.holderName.trim() || account.holderName.length > 50) {
            newErrors['account.holderName'] = '예금주를 입력해주세요 (최대 50자)';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;

        const payload: any = {
            businessType,
            account: {
                bankCode: account.bankCode,
                accountNumber: stripDashes(account.accountNumber),
                holderName: account.holderName.trim(),
            },
        };

        if (isCompanyType) {
            payload.company = {
                name: company.name.trim(),
                representativeName: company.representativeName.trim(),
                businessRegistrationNumber: stripDashes(company.businessRegistrationNumber),
                email: company.email.trim(),
                phone: stripDashes(company.phone),
            };
        } else {
            payload.individual = {
                name: individual.name.trim(),
                email: individual.email.trim(),
                phone: stripDashes(individual.phone),
            };
        }

        // TODO: API 연동
        console.log('[RappoPayDocuments] 제출 데이터:', JSON.stringify(payload, null, 2));
    };

    const updateCompany = (field: keyof CompanyForm, value: string) => {
        setCompany((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => {
            const next = { ...prev };
            delete next[`company.${field}`];
            return next;
        });
    };

    const updateIndividual = (field: keyof IndividualForm, value: string) => {
        setIndividual((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => {
            const next = { ...prev };
            delete next[`individual.${field}`];
            return next;
        });
    };

    const updateAccount = (field: keyof AccountForm, value: string) => {
        setAccount((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => {
            const next = { ...prev };
            delete next[`account.${field}`];
            return next;
        });
    };

    return (
        <Row className="flex gap-[32px] w-full">
            {/* 좌측: 폼 */}
            <Col className="flex flex-col flex-[7]">
                {/* 사업자 유형 선택 */}
                <Col className="flex flex-col mb-[32px]">
                    <MediumText fontSize={18} lineHeight={26} color="#3D4244" className="mb-[16px]">
                        사업자 유형
                    </MediumText>
                    <Row className="flex gap-[16px]">
                        {BUSINESS_TYPES.map((type) => (
                            <label
                                key={type.value}
                                className="flex items-center cursor-pointer"
                                onClick={() => setBusinessType(type.value)}
                            >
                                <div
                                    className={`w-[20px] h-[20px] rounded-full border-[2px] mr-[8px] flex items-center justify-center ${
                                        businessType === type.value
                                            ? 'border-[#3D73DD]'
                                            : 'border-[#D1D5DB]'
                                    }`}
                                >
                                    {businessType === type.value && (
                                        <div className="w-[10px] h-[10px] rounded-full bg-[#3D73DD]" />
                                    )}
                                </div>
                                <RegularText fontSize={15} lineHeight={22} color="#3D4244">
                                    {type.label}
                                </RegularText>
                            </label>
                        ))}
                    </Row>
                </Col>

                {/* 사업자/개인 정보 */}
                <Col className="flex flex-col mb-[32px]">
                    <MediumText fontSize={18} lineHeight={26} color="#3D4244" className="mb-[16px]">
                        {isCompanyType ? '사업자 정보' : '개인 정보'}
                    </MediumText>

                    {isCompanyType ? (
                        <>
                            <FormField label="사업자명" error={errors['company.name']}>
                                <TextInput
                                    value={company.name}
                                    onChange={(v) => updateCompany('name', v)}
                                    placeholder="사업자명을 입력하세요"
                                />
                            </FormField>
                            <FormField label="대표자명" error={errors['company.representativeName']}>
                                <TextInput
                                    value={company.representativeName}
                                    onChange={(v) => updateCompany('representativeName', v)}
                                    placeholder="대표자명을 입력하세요"
                                />
                            </FormField>
                            <FormField label="사업자번호" error={errors['company.businessRegistrationNumber']}>
                                <TextInput
                                    value={company.businessRegistrationNumber}
                                    onChange={(v) => updateCompany('businessRegistrationNumber', v.replace(/[^0-9]/g, ''))}
                                    placeholder="'-' 없이 10자리 숫자"
                                    maxLength={10}
                                />
                            </FormField>
                            <FormField label="이메일" error={errors['company.email']}>
                                <TextInput
                                    value={company.email}
                                    onChange={(v) => updateCompany('email', v)}
                                    placeholder="이메일을 입력하세요"
                                />
                            </FormField>
                            <FormField label="사업자 전화번호" error={errors['company.phone']}>
                                <TextInput
                                    value={company.phone}
                                    onChange={(v) => updateCompany('phone', v.replace(/[^0-9]/g, ''))}
                                    placeholder="'-' 없이 숫자만 입력"
                                />
                            </FormField>
                        </>
                    ) : (
                        <>
                            <FormField label="이름" error={errors['individual.name']}>
                                <TextInput
                                    value={individual.name}
                                    onChange={(v) => updateIndividual('name', v)}
                                    placeholder="이름을 입력하세요"
                                />
                            </FormField>
                            <FormField label="이메일" error={errors['individual.email']}>
                                <TextInput
                                    value={individual.email}
                                    onChange={(v) => updateIndividual('email', v)}
                                    placeholder="이메일을 입력하세요"
                                />
                            </FormField>
                            <FormField label="전화번호" error={errors['individual.phone']}>
                                <TextInput
                                    value={individual.phone}
                                    onChange={(v) => updateIndividual('phone', v.replace(/[^0-9]/g, ''))}
                                    placeholder="'-' 없이 숫자만 입력"
                                />
                            </FormField>
                        </>
                    )}
                </Col>

                {/* 정산 계좌 정보 */}
                <Col className="flex flex-col mb-[32px]">
                    <MediumText fontSize={18} lineHeight={26} color="#3D4244" className="mb-[16px]">
                        정산 계좌 정보
                    </MediumText>

                    <FormField label="은행" error={errors['account.bankCode']}>
                        <select
                            className="border border-[#E8EBF0] rounded-[8px] px-[14px] h-[44px] text-[14px] text-[#3D4244] outline-none focus:border-[#3D73DD] cursor-pointer bg-white"
                            value={account.bankCode}
                            onChange={(e) => updateAccount('bankCode', e.target.value)}
                        >
                            <option value="">은행을 선택하세요</option>
                            {BANK_CODES.map((bank) => (
                                <option key={bank.code} value={bank.code}>
                                    {bank.name}
                                </option>
                            ))}
                        </select>
                    </FormField>
                    <FormField label="계좌번호" error={errors['account.accountNumber']}>
                        <TextInput
                            value={account.accountNumber}
                            onChange={(v) => updateAccount('accountNumber', v.replace(/[^0-9]/g, ''))}
                            placeholder="'-' 없이 숫자만 입력 (최대 14자)"
                            maxLength={14}
                        />
                    </FormField>
                    <FormField label="예금주" error={errors['account.holderName']}>
                        <TextInput
                            value={account.holderName}
                            onChange={(v) => updateAccount('holderName', v)}
                            placeholder="예금주를 입력하세요"
                            maxLength={50}
                        />
                    </FormField>
                </Col>

                {/* 저장 버튼 */}
                <button
                    className="bg-[#3D73DD] rounded-[10px] py-[14px] px-[32px] cursor-pointer self-start mb-[60px] hover:bg-[#2D5FBF] transition-colors"
                    onClick={handleSubmit}
                >
                    <MediumText fontSize={16} lineHeight={24} color="white">
                        저장
                    </MediumText>
                </button>
            </Col>

            {/* 우측: 진행 단계 */}
            <Col className="flex flex-col flex-[3] pt-[8px]">
                <Col className="flex flex-col bg-white border border-[#E8EBF0] rounded-[16px] p-[24px] sticky top-[128px]">
                    <MediumText fontSize={16} lineHeight={24} color="#3D4244" className="mb-[20px]">
                        진행 단계
                    </MediumText>
                    <StepIndicator steps={STEPS} statuses={stepStatuses} />
                </Col>
            </Col>
        </Row>
    );
};

export default RappoPayDocumentsPage;
